import React, { FC, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  MetricCard,
  NO_DATA,
  fmtDay,
  fmtMinutes,
  fmtPct,
  lineOpts,
} from './scalingCard';
import {
  ReleaseMarker,
  RELEASE_MARKER_PLUGINS,
  ReleaseMarkerLegend,
  withReleaseMarkers,
} from './releaseMarkers';

/**
 * How long ops workers hold human tasks, and how much of that time is silent.
 *
 * These two cards are the KPI readout for the idle-task nudge: the hold-time
 * tail (p90, share over the overrun rule) and the idle series should both fall
 * after the go-live marker. Types mirror GET /platform-stats/scaling.
 */

export interface HumanTaskHold {
  definition: string;
  sampleSize: number;
  medianMinutes: number | null;
  p90Minutes: number | null;
  overrunCount: number;
  overrunPct: number | null;
  overrunRule: string;
}

export interface IdleHeldTasksPoint {
  day: string;
  accepted: number;
  crossed: number;
  idleHours: number;
}

export interface IdleHeldTasks {
  definition: string;
  thresholdFloorMinutes: number;
  taskTypes: string[];
  accepted: number;
  crossed: number;
  crossedPct: number | null;
  idleHours: number;
  idleHoursPerDay: number;
  currentlyIdle: number;
  series: IdleHeldTasksPoint[];
}

const fmtHoursShort = (v: number) =>
  `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}h`;

export const HumanTaskHoldCard: FC<{ index: number; data: HumanTaskHold }> = ({
  index,
  data,
}) => (
  <MetricCard
    index={index}
    title='Human task hold time'
    value={fmtMinutes(data.medianMinutes)}
    sub={
      data.sampleSize === 0
        ? 'No tasks were accepted and closed in this window'
        : `Median accepted → closed across ${data.sampleSize} tasks · p90 ${fmtMinutes(
            data.p90Minutes,
          )}`
    }
    details={
      <>
        <p>{data.definition}</p>
        <p>
          Overrun rule: a task counts as held too long once it passes{' '}
          {data.overrunRule} — twice the idle threshold plus a fixed allowance,
          so a long estimate isn&apos;t penalised.
        </p>
      </>
    }
  >
    <div className='scaling-split'>
      <div className='scaling-split-item'>
        <span className='scaling-split-value'>
          {fmtMinutes(data.p90Minutes)}
        </span>
        <span className='scaling-split-label'>p90 hold</span>
      </div>
      <div className='scaling-split-item'>
        <span className='scaling-split-value'>{fmtPct(data.overrunPct)}</span>
        <span className='scaling-split-label'>Held too long</span>
        <span className='scaling-split-meta'>
          {data.overrunCount} of {data.sampleSize} tasks held longer than{' '}
          {data.overrunRule}
        </span>
      </div>
    </div>
  </MetricCard>
);

const idleChartOpts = {
  ...lineOpts,
  plugins: { legend: { display: true, position: 'bottom' as const } },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      position: 'left' as const,
      title: { display: true, text: 'Tasks' },
      grid: { color: 'rgba(148, 163, 184, 0.15)' },
      ticks: { precision: 0 },
    },
    yHours: {
      beginAtZero: true,
      position: 'right' as const,
      title: { display: true, text: 'Idle hours' },
      grid: { drawOnChartArea: false },
    },
  },
};

export const IdleHeldTasksCard: FC<{
  index: number;
  data: IdleHeldTasks;
  releaseMarkers: ReleaseMarker[];
}> = ({ index, data, releaseMarkers }) => {
  const labelDates = useMemo(
    () => data.series.map((p) => p.day),
    [data.series],
  );
  const chartOpts = useMemo(
    () => withReleaseMarkers(idleChartOpts, releaseMarkers, labelDates),
    [releaseMarkers, labelDates],
  );
  const hasSeries = data.series.some((p) => p.accepted > 0);

  return (
    <MetricCard
      index={index}
      title='Idle held tasks'
      value={fmtPct(data.crossedPct)}
      sub={
        data.accepted === 0
          ? 'No tracked tasks were accepted in this window'
          : `${data.crossed} of ${data.accepted} accepted tasks went idle · ${fmtHoursShort(
              data.idleHours,
            )} idle (${fmtHoursShort(data.idleHoursPerDay)}/day) · ${
              data.currentlyIdle
            } idle right now`
      }
      className='scaling-card--wide scaling-card--chart'
      details={
        <>
          <p>{data.definition}</p>
          <p>
            Idle threshold is max({data.thresholdFloorMinutes} min, the task
            estimate). Counted task types: {data.taskTypes.join(', ')}. The
            series is bucketed by the day the task was accepted, so the
            &quot;before&quot; is read straight from history and a go-live
            marker separates it from the &quot;after&quot;.
          </p>
        </>
      }
    >
      {hasSeries && (
        <>
          <div className='scaling-chart scaling-chart--tall'>
            <Line
              options={chartOpts}
              plugins={RELEASE_MARKER_PLUGINS}
              data={{
                labels: data.series.map((p) => fmtDay(p.day)),
                datasets: [
                  {
                    label: 'Tasks that went idle',
                    data: data.series.map((p) => p.crossed),
                    borderColor: 'rgb(217, 119, 6)',
                    backgroundColor: 'rgba(217, 119, 6, 0.15)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                    yAxisID: 'y',
                  },
                  {
                    label: 'Idle hours',
                    data: data.series.map((p) => p.idleHours),
                    borderColor: 'rgb(220, 38, 38)',
                    backgroundColor: 'rgba(220, 38, 38, 0.1)',
                    fill: false,
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                    borderDash: [2, 2],
                    yAxisID: 'yHours',
                  },
                  {
                    label: 'Tasks accepted',
                    data: data.series.map((p) => p.accepted),
                    borderColor: 'rgba(100, 116, 139, 0.6)',
                    backgroundColor: 'rgba(100, 116, 139, 0.05)',
                    fill: false,
                    tension: 0.3,
                    pointRadius: 0,
                    borderWidth: 1,
                    yAxisID: 'y',
                  },
                ],
              }}
            />
          </div>
          <ReleaseMarkerLegend
            markers={releaseMarkers}
            labelDates={labelDates}
          />
        </>
      )}
      {!hasSeries && data.accepted === 0 && (
        <p className='scaling-card-sub'>{NO_DATA}</p>
      )}
    </MetricCard>
  );
};
